import React from 'react';
import { 
  Truck, 
  Package, 
  Building, 
  TrendingUp,
  TrendingDown,
  Activity,
  Clock,
  DollarSign,
  AlertTriangle,
  CheckCircle,
  Target,
  BarChart3,
  Calendar,
  ShoppingCart,
  Users
} from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Area, AreaChart, PieChart, Pie, Cell, LineChart, Line } from 'recharts';
import { useAuth } from '../../contexts/AuthContext';

const SupplyChainDashboard: React.FC = () => {
  const { user } = useAuth();

  // Supply Chain KPI Data
  const supplyChainKPIs = {
    totalSuppliers: 45,
    activeOrders: 127,
    avgLeadTime: 3.2,
    onTimeDelivery: 94.8,
    totalSpend: 2450000,
    costSavings: 185000,
    qualityScore: 96.5,
    riskScore: 12.3
  };

  // Supplier Performance Data
  const supplierPerformance = [
    { name: 'Sigma Healthcare', orders: 450, onTime: 96.8, quality: 98.2, spend: 850000, rating: 'A+', color: '#10B981' },
    { name: 'API', orders: 380, onTime: 94.5, quality: 97.1, spend: 720000, rating: 'A', color: '#8B5CF6' },
    { name: 'Symbion', orders: 290, onTime: 92.1, quality: 95.8, spend: 580000, rating: 'A-', color: '#F59E0B' },
    { name: 'EBOS Group', orders: 220, onTime: 89.7, quality: 94.2, spend: 420000, rating: 'B+', color: '#EF4444' },
    { name: 'Pharma Direct', orders: 180, onTime: 91.3, quality: 96.5, spend: 280000, rating: 'B+', color: '#06B6D4' }
  ];

  // Monthly Procurement Trends
  const procurementTrends = Array.from({ length: 12 }, (_, i) => {
    const month = new Date();
    month.setMonth(month.getMonth() - (11 - i));
    return {
      month: month.toLocaleDateString('en-AU', { month: 'short' }),
      spend: Math.floor(Math.random() * 100000) + 180000,
      orders: Math.floor(Math.random() * 30) + 85,
      savings: Math.floor(Math.random() * 20000) + 12000,
      leadTime: Math.random() * 2 + 2.5
    };
  });

  // Category Spend Distribution
  const categorySpend = [
    { category: 'Cardiovascular', spend: 485000, percentage: 19.8, suppliers: 8, color: '#8B5CF6' },
    { category: 'Endocrine', spend: 398000, percentage: 16.2, suppliers: 6, color: '#10B981' },
    { category: 'Respiratory', spend: 345000, percentage: 14.1, suppliers: 7, color: '#F59E0B' },
    { category: 'Mental Health', spend: 287000, percentage: 11.7, suppliers: 5, color: '#EF4444' },
    { category: 'Gastrointestinal', spend: 245000, percentage: 10.0, suppliers: 6, color: '#06B6D4' },
    { category: 'Other', spend: 690000, percentage: 28.2, suppliers: 13, color: '#84CC16' }
  ];

  // Lead Time Analysis
  const leadTimeData = [
    { range: '1-2 days', orders: 285, percentage: 22.4 },
    { range: '3-5 days', orders: 456, percentage: 35.9 },
    { range: '6-7 days', orders: 298, percentage: 23.4 },
    { range: '8-14 days', orders: 167, percentage: 13.1 },
    { range: '15+ days', orders: 64, percentage: 5.0 }
  ];

  // Supply Chain Risk Indicators
  const riskIndicators = [
    { risk: 'Single Source Dependencies', level: 'Medium', count: 8, impact: 'Moderate supply disruption risk' },
    { risk: 'Quality Issues', level: 'Low', count: 3, impact: 'Minimal impact on operations' },
    { risk: 'Delivery Delays', level: 'Low', count: 5, impact: 'Occasional minor delays' },
    { risk: 'Price Volatility', level: 'High', count: 12, impact: 'Significant cost fluctuations' }
  ];

  // Procurement Performance Metrics
  const procurementMetrics = [
    { metric: 'On-Time Delivery Rate', value: 94.8, target: 95.0, status: 'below' },
    { metric: 'Quality Score', value: 96.5, target: 95.0, status: 'above' },
    { metric: 'Cost Savings vs Budget', value: 7.5, target: 5.0, status: 'above' },
    { metric: 'Supplier Diversity', value: 45, target: 40, status: 'above', unit: ' suppliers' },
    { metric: 'Average Lead Time', value: 3.2, target: 4.0, status: 'above', unit: ' days' },
    { metric: 'Contract Compliance', value: 98.2, target: 98.0, status: 'above' }
  ];

  const StatCard: React.FC<{ 
    title: string; 
    value: string | number; 
    icon: React.ReactNode; 
    trend?: string;
    trendValue?: number;
    color: string;
    bgGradient: string;
    subtitle?: string;
  }> = ({ title, value, icon, trend, trendValue, color, bgGradient, subtitle }) => (
    <div className={`relative overflow-hidden rounded-2xl ${bgGradient} p-6 text-white shadow-lg hover:shadow-xl transition-all duration-300`}>
      <div className="flex items-center justify-between">
        <div className="flex-1">
          <p className="text-white/80 text-sm font-medium mb-1">{title}</p>
          <p className="text-3xl font-bold mb-2">{value}</p>
          {subtitle && <p className="text-white/70 text-xs mb-2">{subtitle}</p>}
          {trend && trendValue !== undefined && (
            <div className="flex items-center space-x-1">
              {trendValue > 0 ? (
                <TrendingUp className="h-4 w-4 text-white/80" />
              ) : (
                <TrendingDown className="h-4 w-4 text-white/80" />
              )}
              <span className="text-white/80 text-sm font-medium">
                {trendValue > 0 ? '+' : ''}{trendValue}% {trend}
              </span>
            </div>
          )}
        </div>
        <div className={`p-4 rounded-2xl ${color} bg-white/20 backdrop-blur-sm`}>
          {icon}
        </div>
      </div>
      <div className="absolute -right-8 -bottom-8 opacity-10">
        <div className="w-24 h-24 rounded-full bg-white/20" />
      </div>
    </div>
  );

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-4xl font-bold bg-gradient-to-r from-gray-900 to-gray-600 bg-clip-text text-transparent">
            Supply Chain Dashboard
          </h1>
          <p className="text-gray-600 mt-2">Procurement analytics and supplier performance metrics for {user?.branch}</p>
        </div>
        <div className="text-right">
          <p className="text-sm text-gray-500">Last updated</p>
          <p className="text-sm font-medium text-gray-900">{new Date().toLocaleString()}</p>
        </div>
      </div>

      {/* Supply Chain KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <StatCard
          title="Total Suppliers"
          value={supplyChainKPIs.totalSuppliers}
          subtitle="Active supplier relationships"
          icon={<Building className="h-6 w-6" />}
          trend="vs last year"
          trendValue={8.9}
          color="text-blue-600"
          bgGradient="bg-gradient-to-br from-blue-500 to-blue-600"
        />
        <StatCard
          title="Annual Spend"
          value={`$${(supplyChainKPIs.totalSpend / 1000000).toFixed(1)}M`}
          subtitle="Total procurement spend"
          icon={<DollarSign className="h-6 w-6" />}
          trend="vs last year"
          trendValue={5.2}
          color="text-green-600"
          bgGradient="bg-gradient-to-br from-green-500 to-green-600"
        />
        <StatCard
          title="On-Time Delivery"
          value={`${supplyChainKPIs.onTimeDelivery}%`}
          subtitle="Supplier performance"
          icon={<Truck className="h-6 w-6" />}
          trend="vs target"
          trendValue={-0.2}
          color="text-purple-600"
          bgGradient="bg-gradient-to-br from-purple-500 to-purple-600"
        />
        <StatCard
          title="Cost Savings"
          value={`$${(supplyChainKPIs.costSavings / 1000).toFixed(0)}K`}
          subtitle="Year-to-date savings"
          icon={<Target className="h-6 w-6" />}
          trend="vs target"
          trendValue={15.8}
          color="text-orange-600"
          bgGradient="bg-gradient-to-br from-orange-500 to-orange-600"
        />
      </div>

      {/* Supply Chain Risk Indicators */}
      <div className="bg-white/70 backdrop-blur-sm rounded-2xl shadow-lg border border-white/20 p-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-3">
            <AlertTriangle className="h-6 w-6 text-orange-600" />
            <div>
              <h3 className="text-xl font-bold text-gray-900">Supply Chain Risk Indicators</h3>
              <p className="text-gray-600 text-sm">Risk assessment and mitigation status</p>
            </div>
          </div>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {riskIndicators.map((risk, index) => (
            <div key={index} className={`p-4 rounded-xl border ${
              risk.level === 'High' ? 'bg-red-50 border-red-200' :
              risk.level === 'Medium' ? 'bg-yellow-50 border-yellow-200' :
              'bg-green-50 border-green-200'
            }`}>
              <div className="flex items-center justify-between mb-2">
                <h4 className="font-semibold text-gray-900">{risk.risk}</h4>
                <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                  risk.level === 'High' ? 'bg-red-100 text-red-800' :
                  risk.level === 'Medium' ? 'bg-yellow-100 text-yellow-800' :
                  'bg-green-100 text-green-800'
                }`}>
                  {risk.level}
                </span>
              </div>
              <p className="text-sm text-gray-600 mb-2">{risk.impact}</p>
              <p className="text-xs text-gray-500">{risk.count} items affected</p>
            </div>
          ))}
        </div>
      </div>

      {/* Main Analytics Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Monthly Procurement Trends */}
        <div className="bg-white/70 backdrop-blur-sm rounded-2xl shadow-lg border border-white/20 p-6">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h3 className="text-xl font-bold text-gray-900">Monthly Procurement Trends</h3>
              <p className="text-gray-600 text-sm">Spend and order volume trends</p>
            </div>
            <BarChart3 className="h-5 w-5 text-gray-600" />
          </div>
          <ResponsiveContainer width="100%" height={300}>
            <AreaChart data={procurementTrends}>
              <defs>
                <linearGradient id="colorSpend" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#8B5CF6" stopOpacity={0.3}/>
                  <stop offset="95%" stopColor="#8B5CF6" stopOpacity={0}/>
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="month" axisLine={false} tickLine={false} />
              <YAxis axisLine={false} tickLine={false} />
              <Tooltip 
                contentStyle={{ 
                  backgroundColor: 'white', 
                  border: 'none', 
                  borderRadius: '12px', 
                  boxShadow: '0 10px 25px rgba(0,0,0,0.1)' 
                }} 
                formatter={(value, name) => [
                  name === 'spend' ? `$${Number(value).toLocaleString()}` : 
                  name === 'savings' ? `$${Number(value).toLocaleString()}` :
                  name === 'leadTime' ? `${Number(value).toFixed(1)} days` : `${value}`,
                  name === 'spend' ? 'Spend' : 
                  name === 'savings' ? 'Savings' :
                  name === 'leadTime' ? 'Lead Time' : 'Orders'
                ]}
              />
              <Area 
                type="monotone" 
                dataKey="spend" 
                stroke="#8B5CF6" 
                strokeWidth={3}
                fillOpacity={1} 
                fill="url(#colorSpend)" 
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>

        {/* Category Spend Distribution */}
        <div className="bg-white/70 backdrop-blur-sm rounded-2xl shadow-lg border border-white/20 p-6">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h3 className="text-xl font-bold text-gray-900">Category Spend Distribution</h3>
              <p className="text-gray-600 text-sm">Procurement spend by therapeutic category</p>
            </div>
          </div>
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={categorySpend}
                cx="50%"
                cy="50%"
                outerRadius={100}
                fill="#8884d8"
                dataKey="spend"
                label={({ category, percentage }) => `${category}: ${percentage}%`}
              >
                {categorySpend.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Pie>
              <Tooltip formatter={(value) => [`$${value.toLocaleString()}`, 'Spend']} />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Procurement Performance Metrics */}
      <div className="bg-white/70 backdrop-blur-sm rounded-2xl shadow-lg border border-white/20 p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h3 className="text-xl font-bold text-gray-900">Procurement Performance Metrics</h3>
            <p className="text-gray-600 text-sm">Key performance indicators vs targets</p>
          </div>
          <Target className="h-5 w-5 text-gray-600" />
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {procurementMetrics.map((metric, index) => (
            <div key={index} className="p-4 bg-gray-50 rounded-xl">
              <div className="flex items-center justify-between mb-3">
                <h4 className="font-medium text-gray-900">{metric.metric}</h4>
                {metric.status === 'above' ? (
                  <CheckCircle className="h-5 w-5 text-green-600" />
                ) : (
                  <AlertTriangle className="h-5 w-5 text-red-600" />
                )}
              </div>
              
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Current:</span>
                  <span className={`text-sm font-medium ${metric.status === 'above' ? 'text-green-600' : 'text-red-600'}`}>
                    {metric.value}{metric.unit || '%'}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Target:</span>
                  <span className="text-sm font-medium text-gray-900">
                    {metric.target}{metric.unit || '%'}
                  </span>
                </div>
                
                <div className="w-full bg-gray-200 rounded-full h-2 mt-2">
                  <div 
                    className={`h-2 rounded-full ${metric.status === 'above' ? 'bg-green-500' : 'bg-red-500'}`}
                    style={{ 
                      width: `${Math.min((metric.value / (metric.target * 1.2)) * 100, 100)}%` 
                    }}
                  ></div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Supplier Performance Table */}
      <div className="bg-white/70 backdrop-blur-sm rounded-2xl shadow-lg border border-white/20 p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h3 className="text-xl font-bold text-gray-900">Supplier Performance</h3>
            <p className="text-gray-600 text-sm">Key supplier metrics and ratings</p>
          </div>
        </div>
        
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50/50">
              <tr>
                <th className="px-6 py-4 text-left text-sm font-medium text-gray-900">Supplier</th>
                <th className="px-6 py-4 text-left text-sm font-medium text-gray-900">Orders</th>
                <th className="px-6 py-4 text-left text-sm font-medium text-gray-900">On-Time %</th>
                <th className="px-6 py-4 text-left text-sm font-medium text-gray-900">Quality Score</th>
                <th className="px-6 py-4 text-left text-sm font-medium text-gray-900">Annual Spend</th>
                <th className="px-6 py-4 text-left text-sm font-medium text-gray-900">Rating</th>
                <th className="px-6 py-4 text-left text-sm font-medium text-gray-900">Performance</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {supplierPerformance.map((supplier, index) => (
                <tr key={index} className="hover:bg-gray-50/50">
                  <td className="px-6 py-4 font-medium text-gray-900">{supplier.name}</td>
                  <td className="px-6 py-4 text-gray-900">{supplier.orders}</td>
                  <td className="px-6 py-4">
                    <span className={`font-medium ${supplier.onTime > 95 ? 'text-green-600' : supplier.onTime > 90 ? 'text-yellow-600' : 'text-red-600'}`}>
                      {supplier.onTime}%
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <span className={`font-medium ${supplier.quality > 97 ? 'text-green-600' : supplier.quality > 95 ? 'text-yellow-600' : 'text-red-600'}`}>
                      {supplier.quality}%
                    </span>
                  </td>
                  <td className="px-6 py-4 font-medium text-gray-900">${supplier.spend.toLocaleString()}</td>
                  <td className="px-6 py-4">
                    <span className={`px-3 py-1 text-sm font-medium rounded-full ${
                      supplier.rating.startsWith('A') ? 'bg-green-100 text-green-800' :
                      supplier.rating.startsWith('B') ? 'bg-yellow-100 text-yellow-800' :
                      'bg-red-100 text-red-800'
                    }`}>
                      {supplier.rating}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center space-x-2">
                      <div className="w-16 bg-gray-200 rounded-full h-2">
                        <div 
                          className="h-2 rounded-full" 
                          style={{ 
                            backgroundColor: supplier.color,
                            width: `${supplier.onTime}%` 
                          }}
                        ></div>
                      </div>
                      <TrendingUp className="h-4 w-4 text-green-500" />
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default SupplyChainDashboard;
